import * as React from "react"
import { format, parse, isValid, startOfDay, endOfDay } from "date-fns"
import { CalendarIcon, Clock } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/shadcn/ui/button"
import { Calendar } from "@/components/shadcn/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/shadcn/ui/popover"
import { Input } from "@/components/shadcn/ui/input"

interface DateTimePickerProps {
  value?: Date | null
  onChange: (date: Date | null) => void
  placeholder?: string
  disabled?: boolean
  maxDate?: Date
  minDate?: Date
  className?: string
}

export function DateTimePicker({
  value,
  onChange,
  placeholder = "Pick a date and time",
  disabled = false,
  maxDate,
  minDate,
  className,
}: DateTimePickerProps) {
  const [open, setOpen] = React.useState(false)
  const [dateInput, setDateInput] = React.useState("")
  const [dateInputError, setDateInputError] = React.useState(false)
  const [month, setMonth] = React.useState<Date>(value ?? new Date())

  React.useEffect(() => {
    if (value) {
      setDateInput(format(value, "MM/dd/yyyy"))
      setDateInputError(false)
    } else {
      setDateInput("")
      setDateInputError(false)
    }
    if (value) {
      setMonth(value)
    }
  }, [value])

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (!selectedDate) {
      onChange(null)
      return
    }

    const newDate = new Date(selectedDate)
    if (value) {
      newDate.setHours(value.getHours(), value.getMinutes(), 0, 0)
    } else {
      newDate.setHours(12, 0, 0, 0)
    }
    onChange(newDate)
  }

  const handleDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    setDateInput(raw)
    setDateInputError(false)
  }

  const handleDateInputBlur = () => {
    if (!dateInput) {
      setDateInputError(false)
      return
    }

    const parsed = parse(dateInput, "MM/dd/yyyy", new Date())
    if (isValid(parsed)) {
      const newDate = new Date(parsed)
      if (value) {
        newDate.setHours(value.getHours(), value.getMinutes(), 0, 0)
      } else {
        newDate.setHours(12, 0, 0, 0)
      }
      if (maxDate && startOfDay(newDate) > endOfDay(maxDate)) {
        setDateInputError(true)
        return
      }
      if (minDate && startOfDay(newDate) < startOfDay(minDate)) {
        setDateInputError(true)
        return
      }
      onChange(newDate)
      setDateInputError(false)
    } else {
      setDateInputError(true)
    }
  }

  const handleDateInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur()
    }
  }

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const timeValue = e.target.value
    if (!timeValue) return

    const [hours, minutes] = timeValue.split(":").map(Number)
    const newDate = value ? new Date(value) : new Date()
    newDate.setHours(hours, minutes, 0, 0)
    onChange(newDate)
  }

  const timeValue = value
    ? `${String(value.getHours()).padStart(2, "0")}:${String(value.getMinutes()).padStart(2, "0")}`
    : ""

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="text"
          placeholder="MM/DD/YYYY"
          value={dateInput}
          onChange={handleDateInputChange}
          onBlur={handleDateInputBlur}
          onKeyDown={handleDateInputKeyDown}
          disabled={disabled}
          className={cn("w-[140px]", dateInputError && "border-red-500")}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            render={
              <Button
                variant="outline"
                disabled={disabled}
                className={cn(
                  "h-auto min-h-10 flex-[1_1_15rem] whitespace-normal justify-start text-left font-normal",
                  !value && "text-muted-foreground",
                  className,
                )}
              />
            }
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? format(value, "PPP 'at' h:mm a") : <span>{placeholder}</span>}
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={value ?? undefined}
              onSelect={handleDateSelect}
              disabled={(date) => {
                if (maxDate && date > maxDate) return true
                if (minDate && date < minDate) return true
                return false
              }}
              month={month}
              onMonthChange={setMonth}
              captionLayout="dropdown-buttons"
              fromYear={1950}
              toYear={new Date().getFullYear() + 1}
              initialFocus
            />
            <div className="border-t p-3 flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <Input
                type="time"
                value={timeValue}
                onChange={handleTimeChange}
                className="w-auto"
                disabled={!value}
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>
      {dateInputError && (
        <span className="text-xs text-red-500">Enter a valid date (MM/DD/YYYY)</span>
      )}
    </div>
  )
}
